/**
 * UI store — modal/popover visibility, loading state, sidebar panels.
 */
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { notifyError } from '../../shared/notify';
import { useDesignStore } from './designStore';

export type ModalName =
  | 'font-picker'
  | 'preview'
  | 'products'
  | 'change-product'
  | 'share'
  | 'login'
  | 'team'
  | 'product-info'
  | 'size-chart'
  | 'print-confirm'
  | 'save'
  | 'help'
  | 'cart-review'
  | 'productGallery'
  | null;

export type PopoverName = 'text' | 'clipart' | 'team' | 'image-attributes' | null;

export type SidebarPanel = 'layers' | null;

export type BuiltInLeftPanelTab = 'product' | 'text' | 'elements' | 'uploads' | 'designs' | 'layers' | 'more';
export type LeftPanelTab = BuiltInLeftPanelTab | (string & {}) | null;

export type BuiltInEditPanelSubView = 'color-list' | 'font-list' | 'effect-list' | 'adjust-list';
export type EditPanelSubView = BuiltInEditPanelSubView | (string & {}) | null;

export type InspectorSectionId =
  | 'content'
  | 'typography'
  | 'color'
  | 'effects'
  | 'image-tools'
  | 'svg-color'
  | 'adjust'
  | 'arrange';

export type MobileInspectorPage = 'properties' | 'font-browser';

export interface ConfirmDialogState {
  visible: boolean;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export type ImageEditorMode = 'crop' | 'erase';

export interface ImageEditorState {
  active: boolean;
  mode: ImageEditorMode;
  itemId: string;
}

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';
export type DesignerStartupPhase = 'booting' | 'hydrating' | 'ready' | 'recoverable-error';

export const useUiStore = defineStore('ui', () => {
  const loading = ref(true);
  const startupPhase = ref<DesignerStartupPhase>('booting');
  const startupError = ref('');
  const startupWarnings = ref<string[]>([]);
  const canvasReady = ref(false);
  /**
   * Bumped to force every addon-rendered layer to re-render. Addon item renderers
   * (text-arc, typography-pro) can register after the first paint, and
   * `CustomItemRenderer` otherwise only re-renders when the item's own content
   * changes — so a layer first painted as plain text would stay plain forever.
   */
  const layerRenderEpoch = ref(0);
  /** Item briefly highlighted on the canvas (e.g. "Show me" from the artwork check). */
  const flashItemId = ref<string | null>(null);
  let flashTimer: ReturnType<typeof setTimeout> | null = null;
  function flashItem(id: string, durationMs = 1600) {
    if (flashTimer) clearTimeout(flashTimer);
    flashItemId.value = id;
    flashTimer = setTimeout(() => {
      flashItemId.value = null;
      flashTimer = null;
    }, durationMs);
  }
  let retryStartupHandler: (() => void | Promise<void>) | null = null;
  const addingToCart = ref(false);
  const saveStatus = ref<SaveStatus>('idle');
  let _saveStatusTimer: ReturnType<typeof setTimeout> | null = null;
  const activeModal = ref<ModalName>(null);
  /** Active view index while Preview modal is open (synced with PreviewModal). */
  const previewModalViewIndex = ref(0);
  const activePopover = ref<PopoverName>(null);
  const sidebarPanel = ref<SidebarPanel>(null);
  const activeLeftPanel = ref<LeftPanelTab>(null);
  const alertMessage = ref('');
  const showAlert = ref(false);
  const editPanelSubView = ref<EditPanelSubView>(null);
  /** Plugin edit panel currently shown full-body inside the Inspector. */
  const inspectorPluginView = ref<string | null>(null);
  /** One-shot request for the Inspector to expand + scroll a section into view. */
  const inspectorFocusSection = ref<InspectorSectionId | null>(null);
  /** Right-hand inspector tab on desktop / tablet: the selected layer, or the layer list. */
  const inspectorTab = ref<'selected' | 'layers'>('selected');
  const activeMobileInspectorSection = ref<InspectorSectionId | null>(null);
  const mobileInspectorPage = ref<MobileInspectorPage>('properties');
  const mobileSheetExpanded = ref(false);
  const confirmDialog = ref<ConfirmDialogState>({ visible: false, message: '', onConfirm: () => {}, onCancel: () => {} });
  const imageEditor = ref<ImageEditorState | null>(null);
  /** True when the user last interacted with the canvas or floating canvas tools. */
  const canvasToolSurfaceActive = ref(true);

  const isImageEditorActive = computed(() => Boolean(imageEditor.value?.active));

  const isModalOpen = computed(() => activeModal.value !== null);
  const isPopoverOpen = computed(() => activePopover.value !== null);
  const isLeftPanelOpen = computed(() => activeLeftPanel.value !== null);
  const showCanvasFloatingTools = computed(() =>
    canvasToolSurfaceActive.value
    && !isModalOpen.value
    && !confirmDialog.value.visible
    && !isImageEditorActive.value
    && !loading.value
    && !addingToCart.value,
  );

  function openModal(name: ModalName) {
    activeModal.value = name;
  }

  function closeModal() {
    activeModal.value = null;
    previewModalViewIndex.value = 0;
  }

  function openPopover(name: PopoverName) {
    activePopover.value = name;
  }

  function closePopover() {
    activePopover.value = null;
    mobileInspectorPage.value = 'properties';
    mobileSheetExpanded.value = false;
  }

  function toggleSidebar(panel: SidebarPanel) {
    sidebarPanel.value = sidebarPanel.value === panel ? null : panel;
  }

  function openLeftPanel(tab: LeftPanelTab) {
    activeLeftPanel.value = tab;
  }

  function closeLeftPanel() {
    activeLeftPanel.value = null;
  }

  function toggleLeftPanel(tab: LeftPanelTab) {
    activeLeftPanel.value = activeLeftPanel.value === tab ? null : tab;
  }

  function setLoading(state: boolean) {
    loading.value = state;
  }

  function setStartupPhase(phase: DesignerStartupPhase) {
    startupPhase.value = phase;
    if (phase !== 'recoverable-error') startupError.value = '';
  }

  function setStartupError(message: string) {
    startupError.value = message;
    startupPhase.value = 'recoverable-error';
  }

  function setStartupWarnings(warnings: string[]) {
    startupWarnings.value = [...warnings];
  }

  function setStartupRetry(handler: (() => void | Promise<void>) | null) {
    retryStartupHandler = handler;
  }

  function retryStartup() {
    return retryStartupHandler?.();
  }

  function setCanvasReady(ready = true) {
    canvasReady.value = ready;
  }

  function bumpLayerRenderEpoch() {
    layerRenderEpoch.value += 1;
  }

  function setAddingToCart(state: boolean) {
    addingToCart.value = state;
  }

  function setSaveStatus(status: SaveStatus) {
    if (_saveStatusTimer) {
      clearTimeout(_saveStatusTimer);
      _saveStatusTimer = null;
    }
    saveStatus.value = status;
    if (status === 'saved' || status === 'error') {
      _saveStatusTimer = setTimeout(() => {
        saveStatus.value = 'idle';
        _saveStatusTimer = null;
      }, 3000);
    }
  }

  function alert(message: string) {
    alertMessage.value = message;
    showAlert.value = false;
    notifyError(message);
  }

  function dismissAlert() {
    showAlert.value = false;
    alertMessage.value = '';
  }

  function setInspectorTab(tab: 'selected' | 'layers') {
    inspectorTab.value = tab;
  }

  function openInspectorSection(id: InspectorSectionId) {
    inspectorTab.value = 'selected';
    inspectorPluginView.value = null;
    inspectorFocusSection.value = id;
    activeMobileInspectorSection.value = id;
    mobileInspectorPage.value = 'properties';
    // On mobile the Inspector renders inside the bottom property drawer,
    // which is driven by the text/image popover names — make sure it's open.
    if (typeof window !== 'undefined' && window.matchMedia('(max-width: 1199px)').matches) {
      const type = useDesignStore().selectedItem?.type;
      if (type) openPopover(type === 'text' ? 'text' : 'image-attributes');
    }
  }

  function clearInspectorFocus() {
    inspectorFocusSection.value = null;
  }

  function openMobileInspector(id: InspectorSectionId) {
    activeMobileInspectorSection.value = id;
    mobileInspectorPage.value = 'properties';
    openInspectorSection(id);
  }

  /** Remembers whether the sheet was tall before the font list grew it, to restore on Back. */
  let sheetExpandedBeforeFonts = false;

  function openMobileFontBrowser() {
    // The half-height sheet leaves room for one or two fonts; the list needs the tall sheet.
    sheetExpandedBeforeFonts = mobileSheetExpanded.value;
    mobileSheetExpanded.value = true;
    mobileInspectorPage.value = 'font-browser';
  }

  function backMobileInspectorPage() {
    if (mobileInspectorPage.value === 'font-browser') mobileSheetExpanded.value = sheetExpandedBeforeFonts;
    mobileInspectorPage.value = 'properties';
  }

  function setMobileSheetExpanded(expanded: boolean) {
    mobileSheetExpanded.value = expanded;
  }

  function openInspectorPluginView(id: string) {
    inspectorPluginView.value = id;
  }

  function closeInspectorPluginView() {
    inspectorPluginView.value = null;
  }

  /**
   * Compatibility shim. Older toolbar code and the public
   * `veyop:ui:setEditSubView` document event used left-panel sub-view names;
   * these now route into the Inspector.
   */
  function setEditSubView(view: EditPanelSubView) {
    editPanelSubView.value = view;
    switch (view) {
      case 'color-list': openInspectorSection('color'); break;
      case 'font-list': openInspectorSection('typography'); break;
      case 'adjust-list': openInspectorSection('adjust'); break;
      case 'effect-list': openInspectorSection('effects'); break;
      case null: closeInspectorPluginView(); break;
      default: openInspectorPluginView(view);
    }
  }

  function showConfirm(message: string, onConfirm: () => void) {
    confirmDialog.value = { visible: true, message, onConfirm, onCancel: () => {} };
  }

  function askConfirm(message: string): Promise<boolean> {
    return new Promise((resolve) => {
      confirmDialog.value = {
        visible: true,
        message,
        onConfirm: () => resolve(true),
        onCancel: () => resolve(false),
      };
    });
  }

  function acceptConfirm() {
    const callback = confirmDialog.value.onConfirm;
    confirmDialog.value = { visible: false, message: '', onConfirm: () => {}, onCancel: () => {} };
    callback();
  }

  function hideConfirm() {
    const callback = confirmDialog.value.onCancel;
    confirmDialog.value = { visible: false, message: '', onConfirm: () => {}, onCancel: () => {} };
    callback();
  }

  function openImageEditor(itemId: string, mode: ImageEditorMode) {
    imageEditor.value = { active: true, mode, itemId };
  }

  function closeImageEditor() {
    imageEditor.value = null;
  }

  function setCanvasToolSurfaceActive(active: boolean) {
    canvasToolSurfaceActive.value = active;
  }

  return {
    loading,
    startupPhase,
    startupError,
    startupWarnings,
    canvasReady,
    layerRenderEpoch,
    flashItemId,
    flashItem,
    addingToCart,
    saveStatus,
    setSaveStatus,
    activeModal,
    previewModalViewIndex,
    activePopover,
    sidebarPanel,
    activeLeftPanel,
    alertMessage,
    showAlert,
    editPanelSubView,
    inspectorPluginView,
    inspectorFocusSection,
    inspectorTab,
    setInspectorTab,
    activeMobileInspectorSection,
    mobileInspectorPage,
    mobileSheetExpanded,
    confirmDialog,
    imageEditor,
    canvasToolSurfaceActive,
    isImageEditorActive,
    isModalOpen,
    isPopoverOpen,
    isLeftPanelOpen,
    showCanvasFloatingTools,
    openModal,
    closeModal,
    openPopover,
    closePopover,
    toggleSidebar,
    openLeftPanel,
    closeLeftPanel,
    toggleLeftPanel,
    setLoading,
    setStartupPhase,
    setStartupError,
    setStartupWarnings,
    setStartupRetry,
    retryStartup,
    setCanvasReady,
    bumpLayerRenderEpoch,
    setAddingToCart,
    alert,
    dismissAlert,
    setEditSubView,
    openInspectorSection,
    clearInspectorFocus,
    openMobileInspector,
    openMobileFontBrowser,
    backMobileInspectorPage,
    setMobileSheetExpanded,
    openInspectorPluginView,
    closeInspectorPluginView,
    showConfirm,
    askConfirm,
    acceptConfirm,
    hideConfirm,
    openImageEditor,
    closeImageEditor,
    setCanvasToolSurfaceActive,
  };
});
